/**
 * Shared DBQ form styles.
 * Used by both DbqFormRenderer (admin preview) and UserDbqEditor (user submission).
 * 
 * Updated to align with Veritas Ratings site styling:
 * - Light gray-blue background matching the main site
 * - Black header with logo
 * - Clean, professional form styling
 */

export const dbqStyles = `
  :root {
    --dbq-bg: rgb(237, 242, 247);
    --dbq-panel: #ffffff;
    --dbq-card: #ffffff;
    --dbq-card-border: rgba(0, 0, 0, 0.12);
    --dbq-accent: #000000;
    --dbq-accent-soft: rgba(0, 0, 0, 0.08);
    --dbq-text: #212529;
    --dbq-muted: #495057;
    --dbq-input-bg: #ffffff;
    --dbq-input-border: rgba(0, 0, 0, 0.15);
    --dbq-focus-ring: rgba(0, 0, 0, 0.15);
  }

  .dbq-renderer {
    min-height: 100vh;
    padding: 2rem 0;
    background: var(--dbq-bg);
  }

  .dbq-panel {
    background: var(--dbq-panel);
    border: 1px solid var(--dbq-card-border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 2rem;
    color: var(--dbq-text);
  }

  .dbq-header h2 {
    font-weight: 600;
    color: var(--dbq-text);
  }

  /* Text Color Fixes for Legibility */
  .dbq-field-description,
  .dbq-field-notes,
  .dbq-instruction {
    color: #4a5568 !important;
  }

  .dbq-note-banner {
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.1);
    color: var(--dbq-text) !important;
  }

  .dbq-alert-info {
    background: rgba(56, 129, 207, 0.08);
    border-color: rgba(56, 129, 207, 0.25);
    color: var(--dbq-text) !important;
  }

  /* Repeating Element Styles */
  .dbq-repeating-wrapper {
    background: rgba(0, 0, 0, 0.02);
    border: 1px solid var(--dbq-card-border);
    border-radius: 12px;
    padding: 1.5rem;
  }

  .dbq-repeating-instance {
    background: #ffffff;
    border: 1px dashed rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    padding: 1.25rem;
  }

  .dbq-section-card {
    background: var(--dbq-card);
    border: 1px solid var(--dbq-card-border);
    border-radius: 12px;
    padding: 2rem;
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.25s ease, border-color 0.25s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .dbq-section-card::before {
    display: none;
  }

  .dbq-section-card:hover {
    transform: none;
    border-color: rgba(0, 0, 0, 0.2);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }

  .dbq-section-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .dbq-section-code {
    background: #000000;
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 999px;
    font-size: 0.85rem;
    letter-spacing: 0.04em;
    color: #ffffff !important;
    font-weight: 600;
  }

  .dbq-section-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #212529 !important;
  }

  .dbq-info-banner {
    background: rgba(56, 129, 207, 0.08);
    border-left: 4px solid #3881cf;
    padding: 1rem 1.25rem;
    border-radius: 8px;
    color: var(--dbq-text);
  }

  .dbq-group-card {
    background: rgba(0, 0, 0, 0.02);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  /* ========================================
     HORIZONTAL LAYOUT (Left/Right side-by-side)
     ======================================== */
  
  .dbq-group-horizontal {
    border-left: 3px solid #3881cf;
  }

  .dbq-horizontal-layout {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  .dbq-horizontal-item {
    min-width: 0;
  }

  /* Make the fields in horizontal layout slightly more compact */
  .dbq-horizontal-item .dbq-field-group {
    margin-bottom: 1rem;
  }

  /* Column headers for Right/Left */
  .dbq-horizontal-item:first-child::before,
  .dbq-horizontal-item:nth-child(2)::before {
    content: none;
  }

  /* Responsive: stack on smaller screens */
  @media (max-width: 768px) {
    .dbq-horizontal-layout {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
    
    .dbq-horizontal-item {
      padding: 0.75rem;
      background: rgba(0, 0, 0, 0.02);
      border-radius: 8px;
    }
  }

  /* Additional elements below horizontal layout */
  .dbq-horizontal-additional {
    grid-column: 1 / -1;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 1rem;
  }

  /* ========================================
     TABLE LAYOUT (for structured data)
     ======================================== */

  .dbq-group-table {
    border-left: 3px solid #1a5f9c;
  }

  .dbq-table-layout {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .dbq-table-item {
    min-width: 0;
  }

  /* Make table fields even more compact */
  .dbq-table-item .dbq-field-group {
    margin-bottom: 0.5rem;
  }

  .dbq-table-item .form-label {
    font-size: 0.85rem;
  }

  .dbq-field-group label {
    color: var(--dbq-text) !important;
    font-weight: 500;
  }

  .dbq-inline-suggestion-toggle,
  .dbq-inline-suggestion-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    border-radius: 999px;
    padding: 0.18rem 0.55rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1.2;
  }

  .dbq-inline-suggestion-toggle {
    border: 1px solid rgba(180, 112, 32, 0.28);
    background: #fff7ed;
    color: #7c3f09;
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  }

  .dbq-inline-suggestion-toggle:hover {
    background: #ffedd5;
    border-color: rgba(180, 112, 32, 0.45);
    color: #6b3206;
  }

  .dbq-inline-suggestion-toggle.is-current {
    background: #eef2f7;
    border-color: rgba(71, 85, 105, 0.25);
    color: #334155;
  }

  .dbq-inline-suggestion-badge {
    background: #fff7ed;
    border: 1px solid rgba(180, 112, 32, 0.22);
    color: #7c3f09;
  }

  .dbq-inline-suggestion-badge--accepted {
    background: #ecfdf3;
    border-color: rgba(22, 163, 74, 0.24);
    color: #166534;
  }

  .dbq-inline-suggestion-badge--ai {
    background: #eff6ff;
    border-color: rgba(37, 99, 235, 0.22);
    color: #1d4ed8;
  }

  .dbq-inline-suggestion-badge--notice {
    background: #eff6ff;
    border-color: rgba(37, 99, 235, 0.2);
    color: #1d4ed8;
  }

  .dbq-inline-suggestion-badge--loading {
    background: #eef2ff;
    border-color: rgba(79, 70, 229, 0.24);
    color: #4338ca;
  }

  .dbq-inline-suggestion-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
    margin: -0.15rem 0 0.75rem;
  }

  .dbq-inline-missing-detail-btn,
  .dbq-inline-suggestion-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    border: 1px solid rgba(217, 119, 6, 0.28);
    background: #fffbeb;
    color: #92400e;
    border-radius: 999px;
    padding: 0.18rem 0.6rem;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1.2;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  }

  .dbq-inline-missing-detail-btn:hover,
  .dbq-inline-suggestion-action-btn:hover:not(:disabled) {
    background: #fef3c7;
    border-color: rgba(217, 119, 6, 0.44);
    color: #78350f;
  }

  .dbq-inline-suggestion-action-btn--evidence {
    border-color: rgba(37, 99, 235, 0.24);
    background: #eff6ff;
    color: #1d4ed8;
  }

  .dbq-inline-suggestion-action-btn--evidence:hover:not(:disabled) {
    background: #dbeafe;
    border-color: rgba(37, 99, 235, 0.4);
    color: #1e40af;
  }

  .dbq-inline-suggestion-action-btn--failure {
    border-color: rgba(220, 38, 38, 0.24);
    background: #fef2f2;
    color: #991b1b;
  }

  .dbq-inline-suggestion-action-btn--failure:hover:not(:disabled) {
    background: #fee2e2;
    border-color: rgba(220, 38, 38, 0.42);
    color: #7f1d1d;
  }

  .dbq-inline-suggestion-action-btn--rejected {
    border-color: rgba(185, 28, 28, 0.22);
    background: #fff1f2;
    color: #9f1239;
  }

  .dbq-inline-suggestion-action-btn--rejected:hover:not(:disabled) {
    background: #ffe4e6;
    border-color: rgba(185, 28, 28, 0.38);
    color: #881337;
  }

  .dbq-inline-suggestion-action-btn:disabled {
    opacity: 0.48;
    cursor: not-allowed;
  }

  .dbq-field-group.dbq-field-inline-accepted {
    border: 1px solid rgba(22, 163, 74, 0.2);
    border-left: 4px solid #16a34a;
    border-radius: 14px;
    background: linear-gradient(90deg, rgba(236, 253, 243, 0.85), rgba(255, 255, 255, 0.95));
    padding: 1rem;
  }

  .dbq-inline-suggestion-help {
    color: #6b7280 !important;
    font-size: 0.78rem;
  }

  /* Ensure all form labels are visible */
  .form-label,
  label {
    color: var(--dbq-text) !important;
  }

  .form-control.modern-input,
  .form-select.modern-input {
    background-color: var(--dbq-input-bg);
    border: 1px solid var(--dbq-input-border);
    color: var(--dbq-text);
    border-radius: 8px;
    padding: 0.75rem 1rem;
    transition: all 0.2s ease;
  }

  .form-control.modern-input:focus {
    background-color: #ffffff;
    border-color: #3881cf;
    box-shadow: 0 0 0 3px rgba(56, 129, 207, 0.15);
  }

  .form-control.modern-input:disabled {
    background-color: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.08);
  }

  /* Auto-resizing textarea for long_text fields */
  textarea.modern-input.dbq-auto-resize {
    min-height: 120px;
    resize: none;
    overflow-y: hidden;
  }

  /* Long text field wrapper with char count */
  .dbq-long-text-wrapper {
    position: relative;
  }

  .dbq-char-count {
    font-size: 0.75rem;
    text-align: right;
    margin-top: 0.35rem;
    padding-right: 0.25rem;
    transition: color 0.2s ease;
  }

  /* Visual feedback when approaching character limit */
  textarea.modern-input.dbq-near-limit {
    border-color: #f59e0b !important;
  }

  textarea.modern-input.dbq-near-limit:focus {
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15) !important;
  }

  textarea.modern-input.dbq-at-limit {
    border-color: #ef4444 !important;
  }

  textarea.modern-input.dbq-at-limit:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15) !important;
  }

  .custom-radio .form-check-input,
  .custom-checkbox .form-check-input {
    background-color: #ffffff;
    border-color: rgba(0, 0, 0, 0.25);
    width: 1.25em;
    height: 1.25em;
    margin-top: 0.15em;
  }

  .custom-radio .form-check-input:checked,
  .custom-checkbox .form-check-input:checked {
    background-color: #3881cf;
    border-color: #3881cf;
    box-shadow: 0 0 0 2px rgba(56, 129, 207, 0.2);
  }

  .form-check-label {
    color: var(--dbq-text) !important;
    padding-left: 0.25rem;
    padding-top: 0.15rem;
  }

  /* Option description styling for single_select and multi_select */
  .dbq-option-description {
    color: #6b7280 !important;
    font-size: 0.85rem;
    line-height: 1.4;
    margin-top: 0.15rem;
    margin-bottom: 0.5rem;
    margin-left: calc(1.5em + 0.5rem); /* Align with label: input width + gap */
  }

  /* Ensure all text in DBQ forms is legible */
  .dbq-renderer {
    color: #212529;
  }

  .dbq-renderer h1,
  .dbq-renderer h2,
  .dbq-renderer h3,
  .dbq-renderer h4,
  .dbq-renderer h5,
  .dbq-renderer h6 {
    color: #212529 !important;
  }

  .dbq-renderer p,
  .dbq-renderer span:not(.dbq-section-code):not(.badge),
  .dbq-renderer strong,
  .dbq-renderer label,
  .dbq-renderer .form-label {
    color: #212529;
  }
  
  /* Ensure badges keep their white text */
  .dbq-renderer .badge,
  .dbq-renderer .dbq-section-code {
    color: #ffffff !important;
  }

  .dbq-toggle .btn {
    border-radius: 999px !important;
    padding: 0.65rem 1.5rem;
    font-weight: 500;
    border: none;
    transition: background 0.3s ease, color 0.3s ease;
  }

  .dbq-toggle .btn-primary {
    background: #000000;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .dbq-toggle .btn-primary:hover {
    background: #333333;
  }

  .dbq-toggle .btn-outline-primary {
    color: var(--dbq-text);
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: transparent;
  }
  
  .dbq-toggle .btn-outline-primary:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--dbq-text);
  }

  .dbq-save-zone {
    background: #ffffff;
    border: 1px solid var(--dbq-card-border);
    border-radius: 12px;
    padding: 2rem;
    text-align: center;
    margin-top: 3rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .dbq-save-btn {
    background: #000000;
    border: none;
    padding: 0.8rem 3rem;
    border-radius: 999px;
    font-weight: 600;
    letter-spacing: 0.02em;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    color: white;
  }

  .dbq-save-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
    background: #333333;
  }

  .dbq-json-card {
    background: #ffffff;
    border: 1px solid var(--dbq-card-border);
    border-radius: 12px;
  }

  pre {
    color: var(--dbq-text);
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  }

  .animate-fade-in {
    animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  /* Set Today Button for Date Signed fields */
  .dbq-set-today-btn {
    white-space: nowrap;
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0.2);
    color: var(--dbq-text);
    padding: 0.4rem 0.75rem;
    font-size: 0.85rem;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .dbq-set-today-btn:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.05);
    border-color: rgba(0, 0, 0, 0.3);
    color: var(--dbq-text);
  }

  .dbq-set-today-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ========================================
     COMMENTS SYSTEM STYLES
     ======================================== */

  /* Layout wrapper for form + comments panel */
  .dbq-layout-wrapper {
    display: flex;
    width: 100%;
    position: relative;
    overflow: hidden;
  }

  /* Form container with animated width */
  .dbq-form-container {
    flex: 1;
    width: 100%;
    transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1), 
                margin-right 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 0;
  }

  .dbq-layout-wrapper.has-comments .dbq-form-container {
    width: 62%;
    margin-right: 2%;
  }

  /* Comments sidebar */
  .dbq-comments-sidebar {
    position: fixed;
    right: 0;
    top: 0;
    width: 36%;
    height: 100vh;
    transform: translateX(100%);
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1000;
    overflow: hidden;
  }

  .dbq-comments-sidebar.visible {
    transform: translateX(0);
  }

  /* ========================================
     MEDICAL OPINION PANEL
     ======================================== */

  .dbq-layout-wrapper.has-comments .dbq-mo-panel {
    display: none;
  }

  .dbq-mo-panel {
    position: fixed;
    right: 20px;
    top: 72px;
    width: 640px;
    max-width: calc(100vw - 32px);
    height: calc(100vh - 88px);
    z-index: 900;
  }

  .dbq-mo-panel.is-hidden {
    display: none;
  }

  .dbq-mo-panel-inner {
    height: 100%;
    background: #ffffff;
    border: 1px solid var(--dbq-card-border);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    padding: 1.25rem;
    overflow: hidden;
  }

  .dbq-mo-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  .dbq-mo-header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .dbq-mo-panel-tabs {
    margin-right: 0.25rem;
  }

  .dbq-mo-header h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  .dbq-mo-section {
    margin-bottom: 0.75rem;
  }

  .dbq-mo-toe-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .dbq-mo-toe-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--dbq-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .dbq-mo-toe .form-select {
    padding: 0.4rem 0.75rem;
    font-size: 0.9rem;
  }

  /* The TOE picker (react-select since 2026-11-04) fills the row. */
  .dbq-mo-toe-row > div:last-child {
    flex: 1;
    min-width: 0;
    font-size: 0.9rem;
  }

  .dbq-mo-context-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .dbq-mo-tabs {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 999px;
    padding: 0.15rem;
  }

  .dbq-mo-tab {
    border: none;
    background: transparent;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--dbq-muted);
    padding: 0.25rem 0.65rem;
    border-radius: 999px;
    cursor: pointer;
  }

  .dbq-mo-tab.active {
    background: #ffffff;
    color: #111111;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .dbq-mo-context-list {
    list-style: none;
    padding-left: 0;
    margin: 0;
    max-height: 120px;
    overflow-y: auto;
  }

  .dbq-mo-context-list li {
    font-size: 0.85rem;
    padding: 4px 0;
    border-bottom: 1px dashed rgba(0, 0, 0, 0.08);
  }

  .dbq-mo-context-file {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .dbq-mo-context-file-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
  }

  .dbq-mo-context-file-name {
    font-weight: 500;
  }

  .dbq-mo-context-pages {
    padding-left: 8px;
    font-size: 0.8rem;
  }

  .dbq-mo-page-score {
    opacity: 0.7;
    margin-left: 1px;
  }

  .dbq-mo-page-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: #3881cf;
    cursor: pointer;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
  }

  .dbq-mo-page-link:hover {
    color: #1a5f9c;
    text-decoration-style: solid;
  }

  .dbq-mo-page-link:focus {
    outline: 2px solid rgba(56, 129, 207, 0.5);
    outline-offset: 1px;
    border-radius: 2px;
  }

  /* Citation links in MO content */
  .dbq-mo-content-wrapper {
    width: 100%;
  }

  .dbq-mo-content-body {
    display: inline;
  }

  .dbq-mo-content-body > * {
    display: inline;
  }

  .dbq-mo-content-body p {
    display: inline;
    margin: 0;
  }

  .dbq-mo-citation-link {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: rgba(56, 129, 207, 0.1);
    border: 1px solid rgba(56, 129, 207, 0.3);
    border-radius: 4px;
    padding: 1px 6px;
    margin: 0 2px;
    font-size: 0.75rem;
    color: #3881cf;
    cursor: pointer;
    transition: all 0.15s ease;
    vertical-align: middle;
  }

  .dbq-mo-citation-link:hover {
    background: rgba(56, 129, 207, 0.2);
    border-color: #3881cf;
    color: #1a5f9c;
  }

  .dbq-mo-citation-icon {
    font-size: 0.7rem;
  }

  .dbq-mo-citation-text {
    font-weight: 500;
  }

  /* Sources section at bottom of message */
  .dbq-mo-sources-section {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px dashed rgba(0, 0, 0, 0.15);
  }

  .dbq-mo-sources-title {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--dbq-muted);
    margin-bottom: 0.5rem;
  }

  .dbq-mo-sources-list {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .dbq-mo-source-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
  }

  .dbq-mo-source-name {
    color: var(--dbq-text);
    font-weight: 500;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dbq-mo-source-pages {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .dbq-mo-source-page-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-size: 0.75rem;
    color: #3881cf;
    cursor: pointer;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
  }

  .dbq-mo-source-page-link:hover {
    color: #1a5f9c;
    text-decoration-style: solid;
  }

  .dbq-mo-source-page-link:not(:last-child)::after {
    content: ',';
    color: var(--dbq-muted);
    margin-right: 2px;
  }

  .dbq-mo-chat {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;
    min-height: 0;
  }

  .dbq-mo-messages {
    flex: 1;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.02);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 14px;
    padding: 0.75rem;
    min-height: 0;
  }

  .dbq-mo-message {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
    margin-bottom: 0.6rem;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .dbq-mo-message.user {
    align-items: flex-end;
  }

  .dbq-mo-message.assistant {
    align-items: flex-start;
  }

  .dbq-mo-message-bubble {
    max-width: 85%;
    padding: 0.6rem 0.8rem;
    border-radius: 14px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    white-space: pre-wrap;
  }

  .dbq-mo-message.user .dbq-mo-message-bubble {
    background: #111111;
    color: #ffffff;
    border-color: #111111;
  }

  .dbq-mo-message.assistant .dbq-mo-message-bubble p {
    margin-bottom: 0.5rem;
  }

  .dbq-mo-message-actions {
    display: flex;
    justify-content: flex-start;
  }

  .dbq-mo-message.user .dbq-mo-message-actions {
    justify-content: flex-end;
  }

  .dbq-mo-input-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .dbq-mo-input-row .form-control {
    flex: 1;
    border-radius: 999px;
    padding: 0.6rem 1rem;
  }

  .dbq-mo-context-panel {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .dbq-mo-context-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.02);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 14px;
    padding: 0.75rem;
  }

  .dbq-mo-context-block + .dbq-mo-context-block {
    margin-top: 1rem;
  }

  .dbq-mo-context-title {
    font-weight: 600;
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }

  .dbq-mo-transcripts-list .dbq-mo-transcript-title {
    font-weight: 600;
    font-size: 0.8rem;
  }

  .dbq-mo-transcripts-list .dbq-mo-transcript-preview {
    font-size: 0.75rem;
    color: var(--dbq-muted);
  }

  /* Auto-investigate toggle */
  .dbq-mo-investigate-toggle {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
    padding: 0.5rem 0;
    border-top: 1px solid var(--dbq-card-border);
  }

  .dbq-mo-investigate-toggle .form-check {
    margin-bottom: 0;
  }

  .dbq-mo-investigate-toggle .form-check-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--dbq-text);
  }

  .dbq-mo-toggle-hint {
    font-size: 0.7rem;
    color: var(--dbq-muted);
  }

  /* Live investigation progress */
  .dbq-mo-investigation-live {
    background: linear-gradient(135deg, #e8f4fd 0%, #f0f7ff 100%);
    border: 1px solid #b8daff;
    border-radius: 8px;
    padding: 0.75rem;
    margin: 0.5rem 0;
  }

  .dbq-mo-investigation-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .dbq-mo-investigation-title {
    font-weight: 600;
    font-size: 0.85rem;
    color: #004085;
  }

  .dbq-mo-investigation-iteration-badge {
    background: #004085;
    color: white;
    font-size: 0.65rem;
    padding: 0.15rem 0.4rem;
    border-radius: 10px;
    margin-left: auto;
  }

  .dbq-mo-investigation-message {
    font-size: 0.8rem;
    color: #004085;
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
  }

  .dbq-mo-investigation-progress-bar {
    height: 4px;
    background: rgba(0, 64, 133, 0.2);
    border-radius: 2px;
    overflow: hidden;
    margin: 0.5rem 0 0.5rem 1.5rem;
  }

  .dbq-mo-investigation-progress-fill {
    height: 100%;
    background: #004085;
    transition: width 0.3s ease;
  }

  .dbq-mo-investigation-result-inline {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    margin-left: 1.5rem;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  .dbq-mo-investigation-result-inline.found {
    background: rgba(40, 167, 69, 0.1);
    color: #28a745;
  }

  .dbq-mo-investigation-result-inline.not-found {
    background: rgba(108, 117, 125, 0.1);
    color: #6c757d;
  }

  .dbq-mo-check {
    color: #28a745;
    font-weight: bold;
  }

  .dbq-mo-x {
    color: #6c757d;
  }

  .dbq-mo-match-preview {
    opacity: 0.8;
    font-style: italic;
  }

  .dbq-mo-questions-preview {
    margin-left: 1.5rem;
    font-size: 0.75rem;
    color: #004085;
  }

  .dbq-mo-question-preview-item {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
    opacity: 0.9;
  }

  .dbq-mo-question-bullet {
    color: #004085;
    font-weight: bold;
  }

  .dbq-mo-question-more {
    opacity: 0.7;
    font-style: italic;
    padding-left: 1rem;
  }

  /* Legacy investigation status (fallback) */
  .dbq-mo-investigation-status {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, #e8f4fd 0%, #f0f7ff 100%);
    border: 1px solid #b8daff;
    border-radius: 8px;
    font-size: 0.85rem;
    color: #004085;
    margin: 0.5rem 0;
  }

  .dbq-mo-investigation-stats {
    font-size: 0.75rem;
    opacity: 0.8;
  }

  /* Investigation summary in context tab */
  .dbq-mo-investigation-summary {
    padding: 0.5rem 0;
  }

  .dbq-mo-investigation-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
  }

  .dbq-mo-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem;
    background: var(--dbq-card-bg);
    border: 1px solid var(--dbq-card-border);
    border-radius: 6px;
  }

  .dbq-mo-stat-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--dbq-primary);
  }

  .dbq-mo-stat-label {
    font-size: 0.65rem;
    color: var(--dbq-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* Investigation log */
  .dbq-mo-investigation-log {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 300px;
    overflow-y: auto;
  }

  .dbq-mo-investigation-entry {
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--dbq-card-border);
  }

  .dbq-mo-investigation-entry:last-child {
    border-bottom: none;
  }

  .dbq-mo-investigation-question {
    font-size: 0.8rem;
    color: var(--dbq-text);
    margin-bottom: 0.25rem;
  }

  .dbq-mo-investigation-iteration {
    font-size: 0.7rem;
    color: var(--dbq-muted);
    margin-right: 0.5rem;
    font-weight: 600;
  }

  .dbq-mo-investigation-result {
    font-size: 0.75rem;
    padding-left: 1.5rem;
  }

  .dbq-mo-evidence-found {
    color: #28a745;
  }

  .dbq-mo-top-match {
    color: var(--dbq-muted);
    font-style: italic;
  }

  .dbq-mo-no-evidence {
    color: var(--dbq-muted);
  }

  /* Manual file additions */
  .dbq-mo-manual-files {
    border-top: 2px solid var(--dbq-primary);
  }

  .dbq-mo-context-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .dbq-mo-add-file-form {
    background: var(--dbq-card-bg);
    border: 1px solid var(--dbq-card-border);
    border-radius: 6px;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .dbq-mo-add-file-form .form-select,
  .dbq-mo-add-file-form .form-control {
    font-size: 0.8rem;
  }

  .dbq-mo-add-file-form textarea {
    resize: vertical;
    min-height: 60px;
  }

  .dbq-mo-add-file-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }

  .dbq-mo-manual-list {
    border-left: 3px solid var(--dbq-primary);
    padding-left: 0.75rem;
  }

  .dbq-mo-manual-file {
    padding: 0.5rem 0;
    border-bottom: 1px dashed var(--dbq-card-border);
  }

  .dbq-mo-manual-file:last-child {
    border-bottom: none;
  }

  .dbq-mo-manual-file-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .dbq-mo-remove-file {
    color: #dc3545;
    font-size: 1.25rem;
    line-height: 1;
    padding: 0 0.25rem;
    text-decoration: none;
  }

  .dbq-mo-remove-file:hover {
    color: #a71d2a;
  }

  .dbq-mo-manual-file-note {
    font-size: 0.75rem;
    color: var(--dbq-text);
    background: rgba(var(--dbq-primary-rgb, 13, 110, 253), 0.05);
    padding: 0.35rem 0.5rem;
    border-radius: 4px;
    margin-top: 0.35rem;
  }

  .dbq-mo-note-label {
    font-weight: 600;
    color: var(--dbq-primary);
  }

  /* Manual page additions (enhanced search flow) */
  .dbq-mo-manual-pages {
    border-top: 2px solid var(--dbq-primary);
  }

  .dbq-mo-manual-count {
    font-size: 0.7rem;
    background: var(--dbq-primary);
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    margin-left: 0.5rem;
  }

  .dbq-mo-selected-pages {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-bottom: 0.75rem;
  }

  .dbq-mo-selected-page {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(56, 129, 207, 0.1);
    border: 1px solid rgba(56, 129, 207, 0.3);
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 0.75rem;
  }

  .dbq-mo-selected-page .dbq-mo-page-link {
    text-decoration: none;
  }

  .dbq-mo-remove-page {
    background: none;
    border: none;
    color: #dc3545;
    font-size: 1rem;
    line-height: 1;
    padding: 0;
    margin-left: 2px;
    cursor: pointer;
  }

  .dbq-mo-remove-page:hover {
    color: #a71d2a;
  }

  .dbq-mo-page-search-form {
    background: var(--dbq-card-bg);
    border: 1px solid var(--dbq-card-border);
    border-radius: 8px;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .dbq-mo-search-step {
    margin-bottom: 0.75rem;
  }

  .dbq-mo-search-step:last-of-type {
    margin-bottom: 0;
  }

  .dbq-mo-search-step-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--dbq-muted);
    margin-bottom: 0.35rem;
  }

  .dbq-mo-file-checkboxes {
    max-height: 120px;
    overflow-y: auto;
    border: 1px solid var(--dbq-card-border);
    border-radius: 4px;
    padding: 0.5rem;
  }

  .dbq-mo-file-checkbox {
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }

  .dbq-mo-file-checkbox:last-child {
    margin-bottom: 0;
  }

  .dbq-mo-file-checkbox .form-check-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .dbq-mo-search-input-row {
    display: flex;
    gap: 0.5rem;
  }

  .dbq-mo-search-input-row .form-control {
    flex: 1;
  }

  .dbq-mo-search-results {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid var(--dbq-card-border);
    border-radius: 4px;
  }

  .dbq-mo-search-result {
    padding: 0.5rem;
    border-bottom: 1px solid var(--dbq-card-border);
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .dbq-mo-search-result:last-child {
    border-bottom: none;
  }

  .dbq-mo-search-result:hover {
    background: rgba(0, 0, 0, 0.03);
  }

  .dbq-mo-search-result.selected {
    background: rgba(56, 129, 207, 0.08);
    border-left: 3px solid #3881cf;
  }

  .dbq-mo-result-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
  }

  .dbq-mo-result-checkbox {
    margin: 0;
  }

  .dbq-mo-result-file {
    font-weight: 500;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dbq-mo-result-page {
    color: #3881cf;
    font-weight: 500;
  }

  .dbq-mo-result-score {
    font-size: 0.7rem;
    color: var(--dbq-muted);
    background: rgba(0, 0, 0, 0.05);
    padding: 1px 4px;
    border-radius: 3px;
  }

  .dbq-mo-result-view {
    background: none;
    border: none;
    color: #3881cf;
    font-size: 0.7rem;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
  }

  .dbq-mo-result-view:hover {
    color: #1a5f9c;
  }

  .dbq-mo-result-preview {
    font-size: 0.7rem;
    color: var(--dbq-muted);
    margin-top: 0.35rem;
    padding-left: 1.75rem;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.4;
  }

  .dbq-mo-search-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--dbq-card-border);
  }

  @media (max-width: 1200px) {
    .dbq-mo-panel {
      left: 16px;
      right: 16px;
      width: auto;
      top: 72px;
    }

    .dbq-mo-panel-inner {
      height: calc(100vh - 120px);
    }
  }

  /* Comments panel content */
  .dbq-comments-panel {
    height: 100%;
    background: #ffffff;
    border-left: 1px solid var(--dbq-card-border);
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
  }

  /* Comments header */
  .dbq-comments-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.02);
  }

  .dbq-comments-title h4 {
    margin: 0 0 0.5rem 0;
    color: var(--dbq-text);
    font-size: 1.1rem;
    font-weight: 600;
  }

  .dbq-comments-field-label {
    display: block;
    color: var(--dbq-muted);
    font-size: 0.85rem;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dbq-comments-nav {
    display: flex;
    gap: 0.25rem;
    align-items: center;
  }

  .dbq-review-tabs {
    display: flex;
    gap: 0.5rem;
    padding: 0.9rem 1rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: #ffffff;
  }

  .dbq-review-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border: 1px solid transparent;
    border-bottom-color: rgba(0, 0, 0, 0.08);
    background: transparent;
    color: var(--dbq-muted);
    border-radius: 0;
    margin-bottom: -1px;
    padding: 0.7rem 1rem 0.75rem;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;
  }

  .dbq-review-tab:hover {
    color: #2b6aa0;
    background: rgba(56, 129, 207, 0.04);
    border-color: rgba(56, 129, 207, 0.18);
    border-bottom-color: rgba(56, 129, 207, 0.18);
  }

  .dbq-review-tab.is-active {
    color: #1d5f96;
    background: #ffffff;
    border-color: rgba(56, 129, 207, 0.22);
    border-bottom-color: #ffffff;
    box-shadow: inset 0 3px 0 #3881cf;
  }

  .dbq-review-tab .badge {
    background: rgba(0, 0, 0, 0.08) !important;
    color: var(--dbq-text);
  }

  .dbq-review-tab.is-active .badge {
    background: rgba(56, 129, 207, 0.12) !important;
    color: #1d5f96;
  }

  .dbq-nav-btn,
  .dbq-close-btn {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--dbq-muted);
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .dbq-nav-btn:hover:not(:disabled),
  .dbq-close-btn:hover {
    color: var(--dbq-text);
    background: rgba(0, 0, 0, 0.05);
    border-color: rgba(0, 0, 0, 0.2);
  }

  .dbq-nav-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  /* Comments list */
  .dbq-comments-list {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  .dbq-no-comments {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--dbq-muted);
  }

  .dbq-no-comments p {
    margin-bottom: 1rem;
  }

  /* Comment card */
  .dbq-comment-card {
    background: rgba(0, 0, 0, 0.02);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    padding: 1rem;
    margin-bottom: 1rem;
    transition: border-color 0.2s ease;
  }

  .dbq-comment-card:hover {
    border-color: rgba(0, 0, 0, 0.15);
  }

  .dbq-comment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .dbq-comment-author {
    color: #3881cf;
    font-weight: 500;
    font-size: 0.9rem;
  }

  .dbq-comment-date {
    color: var(--dbq-muted);
    font-size: 0.75rem;
  }

  .dbq-comment-body {
    color: var(--dbq-text);
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 0.75rem;
  }

  .dbq-suggestion-input-scroll {
    max-height: 16rem;
    overflow-y: auto;
    padding-right: 0.25rem;
  }

  .dbq-comment-actions {
    display: flex;
    gap: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .dbq-action-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
    color: var(--dbq-muted);
    text-decoration: none;
  }

  .dbq-action-btn:hover {
    color: var(--dbq-text);
  }

  .dbq-resolve-btn:hover {
    color: #22c55e;
  }

  /* ===================== */
  /* Suggestion card       */
  /* ===================== */
  .dbq-suggestion-card {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 16px;
    padding: 0;
    margin-bottom: 1rem;
    overflow: hidden;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  }

  .dbq-suggestion-card:hover {
    border-color: rgba(56, 129, 207, 0.28);
    box-shadow: 0 16px 34px rgba(56, 129, 207, 0.12);
    transform: translateY(-1px);
  }

  .dbq-suggestion-card--pending {
    border-left: 3px solid #3881cf;
  }

  .dbq-suggestion-card--accepted {
    border-left: 3px solid #22c55e;
  }

  .dbq-suggestion-card--rejected {
    border-left: 3px solid #ef4444;
  }

  .dbq-suggestion-card--dismissed {
    border-left: 3px solid #94a3b8;
  }

  .dbq-suggestion-card--notice-warning {
    border-left: 3px solid #f59e0b;
    background: linear-gradient(180deg, rgba(255, 251, 235, 0.96), #ffffff 36%);
  }

  .dbq-suggestion-card--notice-info {
    border-left: 3px solid #0ea5e9;
    background: linear-gradient(180deg, rgba(240, 249, 255, 0.96), #ffffff 36%);
  }

  .dbq-suggestion-card--notice-progress {
    border-left: 3px solid #6366f1;
    background: linear-gradient(180deg, rgba(238, 242, 255, 0.96), #ffffff 36%);
  }

  .dbq-suggestion-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem 1.25rem 0.35rem;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.92), rgba(248, 250, 252, 0));
  }

  .dbq-suggestion-card-meta {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
  }

  .dbq-suggestion-card-author {
    font-weight: 600;
    font-size: 1.05rem;
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: var(--dbq-text);
  }

  .dbq-suggestion-card-date {
    font-size: 0.78rem;
    color: var(--dbq-muted);
  }

  .dbq-suggestion-card-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #2563eb;
  }

  .dbq-suggestion-card-eyebrow--ai {
    color: #1d4ed8;
  }

  .dbq-suggestion-card-eyebrow-icon {
    font-size: 0.78rem;
  }

  .dbq-suggestion-card-eyebrow--warning {
    color: #b45309;
  }

  .dbq-suggestion-card-eyebrow--info {
    color: #0369a1;
  }

  .dbq-suggestion-card-eyebrow--progress {
    color: #4338ca;
  }

  .dbq-suggestion-card-status-text {
    flex-shrink: 0;
    padding-top: 0.15rem;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: #475569;
  }

  .dbq-suggestion-card-status-text--pending {
    color: #2563eb;
  }

  .dbq-suggestion-card-status-text--accepted {
    color: #15803d;
  }

  .dbq-suggestion-card-status-text--rejected {
    color: #b91c1c;
  }

  .dbq-suggestion-card-status-text--dismissed {
    color: #64748b;
  }

  .dbq-suggestion-card-status-text--notice-warning {
    color: #b45309;
  }

  .dbq-suggestion-card-status-text--notice-info {
    color: #0369a1;
  }

  .dbq-suggestion-card-status-text--notice-progress {
    color: #4338ca;
  }

  .dbq-suggestion-card-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem 0;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--dbq-muted);
  }

  .dbq-suggestion-card-field-type {
    font-weight: 500;
    text-transform: capitalize;
    letter-spacing: normal;
    font-size: 0.7rem;
    color: #94a3b8;
    background: rgba(0, 0, 0, 0.04);
    padding: 0.15em 0.5em;
    border-radius: 4px;
  }

  .dbq-suggestion-card-body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.45rem 1.25rem 1.1rem;
  }

  /* Value display: tag list for multi_select, single_select, yes_no */
  .dbq-suggestion-value-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .dbq-suggestion-value-tag {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: #e8f0fe;
    color: #1a5282;
    font-size: 0.82rem;
    font-weight: 500;
    padding: 0.3rem 0.65rem;
    border-radius: 6px;
    border: 1px solid rgba(56, 129, 207, 0.15);
    line-height: 1.3;
  }

  .dbq-suggestion-value-check {
    color: #22c55e;
    font-size: 0.75rem;
    font-weight: 700;
  }

  .dbq-suggestion-value-tag--single {
    background: #f0f4ff;
    color: #1a5282;
  }

  .dbq-suggestion-value-arrow {
    font-size: 0.65rem;
    opacity: 0.6;
  }

  .dbq-suggestion-value-tag--yes {
    background: #dcfce7;
    color: #166534;
    border-color: rgba(34, 197, 94, 0.2);
  }

  .dbq-suggestion-value-tag--no {
    background: #fef2f2;
    color: #991b1b;
    border-color: rgba(239, 68, 68, 0.2);
  }

  .dbq-suggestion-value-text {
    font-size: 0.9rem;
    color: var(--dbq-text);
    line-height: 1.6;
    word-break: break-word;
    white-space: break-spaces;
  }

  /* Note display */
  .dbq-suggestion-card-note {
    padding: 0.85rem 1.25rem 0;
    font-size: 0.9rem;
    color: #475569;
    border-top: 1px solid rgba(15, 23, 42, 0.06);
    background: transparent;
    line-height: 1.55;
  }

  .dbq-suggestion-card-note-label {
    display: block;
    margin-bottom: 0.3rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #475569;
  }

  .dbq-suggestion-card-decision-note {
    color: #334155;
  }

  /* Action buttons */
  .dbq-suggestion-card-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    padding: 0.95rem 1.25rem 1rem;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    background: rgba(248, 250, 252, 0.9);
  }

  .dbq-suggestion-action-btn {
    font-size: 0.82rem;
    font-weight: 600;
    padding: 0.4rem 0.85rem;
    border-radius: 10px;
  }

  .dbq-suggestion-card-section-title {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
  }

  .dbq-suggestion-question-context {
    display: flex;
    align-items: flex-start;
    gap: 0.45rem;
    max-width: 34rem;
  }

  .dbq-suggestion-question-number {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.85rem;
    height: 1.35rem;
    padding: 0 0.38rem;
    border-radius: 999px;
    background: rgba(56, 129, 207, 0.1);
    color: #1a5282;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    line-height: 1;
  }

  .dbq-suggestion-question-text {
    color: #334155;
    font-size: 0.76rem;
    font-weight: 650;
    line-height: 1.35;
  }

  .dbq-suggestion-card-section-text {
    font-size: 0.92rem;
    line-height: 1.55;
    color: #475569;
    max-width: 34rem;
  }

  .dbq-suggestion-answer-gateway {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.7rem;
  }

  .dbq-suggestion-answer-btn {
    flex-shrink: 0;
    align-self: flex-start;
    border-radius: 10px;
    padding: 0.45rem 0.9rem;
    font-weight: 600;
  }

  .dbq-suggestion-missing-inline {
    display: flex;
    flex-direction: column;
    gap: 0.22rem;
    width: 100%;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(245, 158, 11, 0.09), rgba(245, 158, 11, 0.04));
    border: 1px solid rgba(245, 158, 11, 0.18);
  }

  .dbq-suggestion-missing-inline-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
  }

  .dbq-suggestion-missing-inline-text {
    font-size: 0.9rem;
    line-height: 1.45;
    color: #78350f;
  }

  .dbq-suggestion-provenance {
    display: flex;
    flex-direction: column;
    gap: 0.95rem;
    padding-top: 0.95rem;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
  }

  .dbq-suggestion-provenance-section {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .dbq-suggestion-provenance-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
  }

  .dbq-suggestion-provenance-text {
    font-size: 0.92rem;
    line-height: 1.6;
    color: #475569;
  }

  .dbq-suggestion-evidence-list,
  .dbq-suggestion-transcript-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .dbq-suggestion-evidence-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .dbq-suggestion-evidence-file,
  .dbq-suggestion-evidence-page {
    width: fit-content;
    padding: 0;
    border: none;
    background: transparent;
    color: #2563eb;
    font-size: 0.92rem;
    line-height: 1.45;
    text-align: left;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
  }

  .dbq-suggestion-evidence-file {
    font-weight: 600;
  }

  .dbq-suggestion-evidence-file--static {
    color: #1f2937;
    text-decoration: none;
    cursor: default;
  }

  .dbq-suggestion-evidence-pages {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
  }

  .dbq-suggestion-transcript-item {
    font-size: 0.92rem;
    line-height: 1.6;
    color: #475569;
  }

  .dbq-suggestion-evidence-file:hover,
  .dbq-suggestion-evidence-page:hover {
    color: #1d4ed8;
  }

  .dbq-suggestion-missing-callout {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 1rem 1.05rem;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(245, 158, 11, 0.1), rgba(255, 255, 255, 0.96));
    border: 1px solid rgba(245, 158, 11, 0.18);
  }

  .dbq-suggestion-missing-callout-title {
    font-size: 0.94rem;
    font-weight: 700;
    color: #92400e;
  }

  .dbq-suggestion-missing-callout-text {
    font-size: 0.92rem;
    line-height: 1.5;
    color: #78350f;
  }

  .dbq-suggestion-missing-fields {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .dbq-suggestion-missing-field {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }

  .dbq-suggestion-missing-field-label {
    font-size: 0.8rem;
    font-weight: 700;
    color: #1f2937;
  }

  .dbq-suggestion-missing-field-context {
    font-size: 0.8rem;
    line-height: 1.45;
    color: #374151;
    background: rgba(15, 23, 42, 0.04);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 10px;
    padding: 0.55rem 0.7rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  }

  .dbq-suggestion-missing-field-help {
    font-size: 0.86rem;
    line-height: 1.45;
    color: #5b6472;
  }

  .dbq-suggestion-missing-search-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.65rem;
  }

  .dbq-suggestion-missing-search-row .btn {
    border-radius: 10px;
    font-weight: 650;
  }

  .dbq-suggestion-missing-search-message {
    flex: 1;
    min-width: 12rem;
    font-size: 0.82rem;
    line-height: 1.45;
    color: #475569;
  }

  .dbq-suggestion-missing-search-message--found {
    color: #166534;
  }

  .dbq-suggestion-missing-search-message--not_found,
  .dbq-suggestion-missing-search-message--error {
    color: #9a3412;
  }

  .dbq-suggestion-missing-field-actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.65rem;
    align-items: center;
  }

  @media (max-width: 640px) {
    .dbq-suggestion-card-header,
    .dbq-suggestion-answer-gateway,
    .dbq-suggestion-card-actions {
      flex-direction: column;
      align-items: flex-start;
    }

    .dbq-suggestion-card-status-text {
      padding-top: 0;
    }

    .dbq-suggestion-action-btn.ms-auto {
      margin-left: 0 !important;
    }

    .dbq-suggestion-missing-field-actions {
      grid-template-columns: 1fr;
    }
  }

  /* Replies */
  .dbq-replies {
    margin-top: 0.75rem;
    padding-left: 1rem;
    border-left: 2px solid rgba(0, 0, 0, 0.1);
  }

  .dbq-reply {
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
  }

  .dbq-reply:last-child {
    margin-bottom: 0;
  }

  .dbq-reply-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.35rem;
  }

  .dbq-reply-author {
    color: #1a5f9c;
    font-weight: 500;
    font-size: 0.85rem;
  }

  .dbq-reply-date {
    color: var(--dbq-muted);
    font-size: 0.7rem;
  }

  .dbq-reply-body {
    color: var(--dbq-text);
    font-size: 0.9rem;
    line-height: 1.4;
  }

  /* Reply input */
  .dbq-reply-input {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .dbq-reply-textarea {
    background: #ffffff !important;
    border: 1px solid rgba(0, 0, 0, 0.15) !important;
    color: var(--dbq-text) !important;
    border-radius: 8px;
    font-size: 0.9rem;
    resize: none;
  }

  .dbq-reply-textarea:focus {
    border-color: #3881cf !important;
    box-shadow: 0 0 0 3px rgba(56, 129, 207, 0.15) !important;
  }

  .dbq-reply-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  /* Add comment trigger */
  .dbq-add-comment-trigger {
    padding: 1rem;
    text-align: center;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  /* New comment input */
  .dbq-new-comment {
    padding: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.02);
  }

  .dbq-new-comment-textarea {
    background: #ffffff !important;
    border: 1px solid rgba(0, 0, 0, 0.15) !important;
    color: var(--dbq-text) !important;
    border-radius: 8px;
    font-size: 0.9rem;
    resize: none;
    margin-bottom: 0.5rem;
  }

  .dbq-new-comment-textarea:focus {
    border-color: #3881cf !important;
    box-shadow: 0 0 0 3px rgba(56, 129, 207, 0.15) !important;
  }

  .dbq-new-comment-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  /* Required toggle button for admins */
  .dbq-required-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--dbq-muted);
    padding: 0.25rem 0.4rem;
    border-radius: 6px;
    font-size: 0.7rem;
    cursor: pointer;
    transition: all 0.2s ease;
    opacity: 0.4;
  }

  .dbq-required-toggle:hover {
    opacity: 1;
    color: #dc2626;
    background: rgba(220, 38, 38, 0.1);
  }

  .dbq-required-toggle.is-required {
    opacity: 1;
    color: #dc2626;
    background: rgba(220, 38, 38, 0.1);
  }

  .dbq-required-toggle.is-required:hover {
    color: #b91c1c;
    background: rgba(220, 38, 38, 0.15);
  }

  /* Comment button on field labels */
  .dbq-comment-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    background: transparent;
    border: none;
    color: var(--dbq-muted);
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
    opacity: 0.5;
  }

  .dbq-comment-btn:hover {
    opacity: 1;
    color: var(--dbq-text);
    background: rgba(0, 0, 0, 0.05);
  }

  .dbq-comment-btn.has-comments {
    opacity: 1;
    color: #3881cf;
  }

  .dbq-comment-btn.has-comments:hover {
    color: #1a5f9c;
    background: rgba(56, 129, 207, 0.1);
  }

  .dbq-suggestion-btn.has-comments {
    color: #d97706;
  }

  .dbq-suggestion-btn.has-comments:hover {
    color: #b45309;
    background: rgba(217, 119, 6, 0.1);
  }

  .dbq-suggestion-btn .dbq-comment-badge {
    background: #d97706;
  }

  .dbq-comment-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    background: #3881cf;
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    border-radius: 999px;
    padding: 0 5px;
  }

  /* Comments notification banner */
  .dbq-comments-notification {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(251, 191, 36, 0.12);
    border: 1px solid rgba(217, 119, 6, 0.3);
    color: #b45309;
    padding: 0.75rem 1.25rem;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 1rem;
  }

  .dbq-comments-notification:hover {
    background: rgba(251, 191, 36, 0.2);
    border-color: rgba(217, 119, 6, 0.5);
    transform: translateY(-1px);
  }

  .dbq-notification-action {
    margin-left: auto;
    font-size: 0.85rem;
    opacity: 0.7;
    font-weight: 400;
  }

  .dbq-comments-notification:hover .dbq-notification-action {
    opacity: 1;
  }

  /* Active comment field highlight */
  .dbq-field-active-comment {
    position: relative;
  }

  .dbq-field-active-comment::before {
    content: '';
    position: absolute;
    left: -1rem;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #3881cf;
    border-radius: 2px;
    animation: pulse-glow 2s ease-in-out infinite;
  }

  @keyframes pulse-glow {
    0%, 100% {
      opacity: 0.6;
      box-shadow: 0 0 6px rgba(56, 129, 207, 0.3);
    }
    50% {
      opacity: 1;
      box-shadow: 0 0 12px rgba(56, 129, 207, 0.5);
    }
  }

  /* Responsive adjustments for comments panel */
  @media (max-width: 1200px) {
    .dbq-comments-sidebar {
      width: 40%;
    }
    
    .dbq-layout-wrapper.has-comments .dbq-form-container {
      width: 58%;
    }
  }

  @media (max-width: 992px) {
    .dbq-comments-sidebar {
      width: 50%;
    }
    
    .dbq-layout-wrapper.has-comments .dbq-form-container {
      width: 48%;
    }
  }

  @media (max-width: 768px) {
    .dbq-comments-sidebar {
      width: 100%;
    }
    
    .dbq-layout-wrapper.has-comments .dbq-form-container {
      width: 100%;
      margin-right: 0;
    }
  }
`;

// Standalone styles (for UserDbqEditor without PrivateLayout wrapper)
export const dbqStandaloneStyles = `
  body { margin: 0; padding: 0; }

  .dbq-renderer {
    min-height: 100vh;
    padding: 2rem;
  }

  .dbq-header { 
    margin-bottom: 2rem; 
    text-align: center; 
  }
  
  .dbq-header h1 { 
    font-weight: 600; 
    color: var(--dbq-text); 
    margin-bottom: 0.5rem; 
  }
  
  .dbq-header p { 
    color: var(--dbq-muted); 
  }

  .dbq-section-card {
    max-width: 1200px;
    margin: 0 auto 2rem auto;
  }

  .dbq-save-zone {
    margin: 3rem auto;
    max-width: 1200px;
  }

  /* Adjust max-width when comments panel is open */
  .dbq-layout-wrapper.has-comments .dbq-section-card,
  .dbq-layout-wrapper.has-comments .dbq-save-zone {
    max-width: 100%;
  }

  .dbq-layout-wrapper.has-comments .dbq-header {
    text-align: left;
  }

  /* ===== Validation Panel (missing required fields) ===== */
  .dbq-validation-panel {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 12px;
    margin-top: 1rem;
    overflow: hidden;
  }

  .dbq-validation-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: #fee2e2;
    border-bottom: 1px solid #fecaca;
  }

  .dbq-validation-panel-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    font-size: 0.9rem;
    color: #991b1b;
  }

  .dbq-validation-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    background: #dc2626;
    color: white;
    border-radius: 50%;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;
  }

  .dbq-validation-panel-close {
    background: none;
    border: none;
    color: #991b1b;
    font-size: 1.3rem;
    cursor: pointer;
    padding: 0 0.25rem;
    line-height: 1;
    opacity: 0.6;
    transition: opacity 0.15s;
  }

  .dbq-validation-panel-close:hover {
    opacity: 1;
  }

  .dbq-validation-panel-body {
    padding: 0.75rem 1rem;
    max-height: 280px;
    overflow-y: auto;
  }

  .dbq-validation-section {
    margin-bottom: 0.75rem;
  }

  .dbq-validation-section:last-child {
    margin-bottom: 0;
  }

  .dbq-validation-section-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #b91c1c;
    margin-bottom: 0.35rem;
    padding-left: 0.25rem;
  }

  .dbq-validation-field-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .dbq-validation-field-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    padding: 0.4rem 0.5rem;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    font-size: 0.85rem;
    color: #7f1d1d;
    transition: background 0.15s;
    width: 100%;
  }

  .dbq-validation-field-item:hover {
    background: rgba(220, 38, 38, 0.08);
  }

  .dbq-validation-field-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #dc2626;
    flex-shrink: 0;
  }

  .dbq-validation-field-label {
    flex-grow: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dbq-validation-field-action {
    font-size: 0.75rem;
    color: #dc2626;
    font-weight: 500;
    opacity: 0;
    transition: opacity 0.15s;
    flex-shrink: 0;
  }

  .dbq-validation-field-item:hover .dbq-validation-field-action {
    opacity: 1;
  }

  /* Missing field highlight flash */
  @keyframes missing-field-flash {
    0% { background: rgba(220, 38, 38, 0.15); }
    50% { background: rgba(220, 38, 38, 0.05); }
    100% { background: transparent; }
  }

  .dbq-field-missing-highlight {
    animation: missing-field-flash 2s ease-out;
    border-radius: 8px;
  }

  /* ===== Review Notification Bar (comments + suggestions) ===== */
  .dbq-review-notification-bar {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
    flex-wrap: wrap;
  }

  .dbq-review-notification-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1rem;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    font-size: 0.88rem;
    font-weight: 500;
    transition: all 0.2s ease;
    flex: 1;
    min-width: 160px;
  }

  .dbq-review-notification-item::after {
    content: '→';
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 700;
    opacity: 0.62;
    transition: transform 0.18s ease, opacity 0.18s ease, background-color 0.18s ease;
  }

  .dbq-review-notification-comments {
    background: rgba(56, 129, 207, 0.1);
    color: #1e5a96;
    border: 1px solid rgba(56, 129, 207, 0.2);
  }

  .dbq-review-notification-comments:hover {
    background: rgba(56, 129, 207, 0.18);
    border-color: rgba(56, 129, 207, 0.35);
    transform: translateY(-1px);
  }

  .dbq-review-notification-comments::after {
    background: rgba(56, 129, 207, 0.12);
  }

  .dbq-review-notification-suggestions {
    background: rgba(245, 158, 11, 0.1);
    color: #92400e;
    border: 1px solid rgba(245, 158, 11, 0.2);
  }

  .dbq-review-notification-suggestions:hover {
    background: rgba(245, 158, 11, 0.18);
    border-color: rgba(245, 158, 11, 0.35);
    transform: translateY(-1px);
  }

  .dbq-review-notification-suggestions::after {
    background: rgba(245, 158, 11, 0.14);
  }

  .dbq-review-notification-item:hover::after {
    opacity: 0.92;
    transform: translateX(2px);
  }

  .dbq-review-notification-icon {
    font-size: 0.9rem;
    opacity: 0.8;
  }

  .dbq-review-notification-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 5px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
  }

  .dbq-review-notification-comments .dbq-review-notification-count {
    background: #3881cf;
    color: white;
  }

  .dbq-review-notification-suggestions .dbq-review-notification-count {
    background: #d97706;
    color: white;
  }

  .dbq-review-notification-label {
    font-weight: 500;
  }

  .dbq-review-notification-item {
    flex-wrap: nowrap;
  }

  .dbq-suggestion-filter-select {
    max-width: 200px;
    font-size: 0.85rem;
    margin-left: 1rem;
  }
`;

export default dbqStyles;
